import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Download, Mail, Clock, User, CreditCard, FileText, MapPin, Phone } from 'lucide-react';
import axios from 'axios';
import moment from 'moment';
import { API_BASE_URL } from './apiConfig';
import { useReactToPrint } from 'react-to-print';
import { trackEvent } from './lib/analytics';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = React.useRef(null);

  const txnid = searchParams.get('txnid');
  const bookingId = searchParams.get('booking_id');
  const trackedTxnRef = useRef(null);

  useEffect(() => {
    if (!txnid) {
      setLoading(false);
      return;
    }

    const fetchBookingDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/payment/status/${txnid}`);
        if (response.data.success) {
          setBooking(response.data.booking);
          if (trackedTxnRef.current !== txnid) {
            trackedTxnRef.current = txnid;
            const b = response.data.booking;
            trackEvent('purchase', {
              transaction_id: txnid,
              currency: 'INR',
              value: b.total_amount || b.amount || 0,
              payment_status: b.payment_status,
              item_id: bookingId || undefined,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching booking details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [txnid, bookingId]);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: `Receipt-${txnid || 'Booking'}`,
  });

  const formatDuration = (d) => {
    if (!d) return '';
    const str = d.toString().toLowerCase();
    if (str.includes('hr')) return d.toString().replace(/hrhr/g, 'hr');
    return `${d}hr`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4ECDE] flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#471F21]"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#F4ECDE] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-[#471F21]/10">
          <div className="w-16 h-16 bg-[#471F21]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CreditCard className="w-8 h-8 text-[#471F21]" />
          </div>
          <h2 className="text-2xl font-bold text-[#471F21] mb-2">Booking Not Found</h2>
          <p className="text-slate-600 mb-8">We couldn't find a booking with transaction ID: {txnid}</p>
          <Link
            to="/"
            className="inline-block w-full bg-[#471F21] text-white py-3 rounded-xl font-semibold hover:bg-[#5a3a3c] transition-colors"
          >
            Go Back Home
          </Link>
        </div>
      </div>
    );
  }

  const isPending = booking.payment_status === 'pending';

  return (
    <div className="min-h-screen bg-[#F4ECDE] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          {isPending ? (
            <>
              <h1 className="text-4xl font-bold text-[#471F21] mb-4">Payment Received!</h1>
              <p className="text-xl text-[#5a3a3c]">We're confirming your payment with the bank — this can take a few minutes. You'll get a confirmation email and WhatsApp message shortly.</p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-[#471F21] mb-4">Payment Successful!</h1>
              <p className="text-xl text-[#5a3a3c]">Your photoshoot session has been confirmed.</p>
            </>
          )}
        </div>

        {/* Receipt Card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-[#471F21]/10" ref={contentRef}>
          {/* Header */}
          <div className="bg-[#471F21] px-8 py-8 flex justify-between items-center">
            <div className="text-[#F4ECDE]">
              <h2 className="text-2xl font-bold font-serif">Booking Receipt</h2>
              <p className="text-[#F4ECDE]/70 text-sm mt-1">Transaction ID: {txnid}</p>
            </div>
            <img src="/logo-light.svg" alt="TTLY" className="h-10" />
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 border-b border-[#F4ECDE] pb-8">
              {/* Customer Info */}
              <div>
                <h3 className="text-xs font-bold text-[#471F21]/50 uppercase tracking-widest mb-4">Customer Details</h3>
                <div className="space-y-4">
                  <div className="flex items-center text-[#5a3a3c]">
                    <User className="w-5 h-5 mr-3 text-[#471F21]/40" />
                    <span className="font-semibold">{booking.user_name}</span>
                  </div>
                  <div className="flex items-center text-[#5a3a3c]">
                    <Mail className="w-5 h-5 mr-3 text-[#471F21]/40" />
                    <span>{booking.user_email}</span>
                  </div>
                  <div className="flex items-center text-[#5a3a3c]">
                    <Phone className="w-5 h-5 mr-3 text-[#471F21]/40" />
                    <span>{booking.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Booking Info */}
              <div>
                <h3 className="text-xs font-bold text-[#471F21]/50 uppercase tracking-widest mb-4">Booking Details</h3>
                <div className="space-y-4">
                  <div className="flex items-center text-[#5a3a3c]">
                    <Calendar className="w-5 h-5 mr-3 text-[#471F21]/40" />
                    <span className="font-semibold">{moment(booking.date || booking.slot_time).utcOffset(330).format('DD MMMM YYYY')}</span>
                  </div>
                  <div className="flex items-center text-[#5a3a3c]">
                    <Clock className="w-5 h-5 mr-3 text-[#471F21]/40" />
                    <span>{booking.guest_use_time || `${moment(booking.slot_time).utcOffset(330).format('hh:mm A')} - ${moment(booking.slot_time).utcOffset(330).add(parseFloat(booking.duration), 'hours').format('hh:mm A')}`} ({formatDuration(booking.duration)})</span>
                  </div>
                  <div className="flex items-center text-[#5a3a3c]">
                    <MapPin className="w-5 h-5 mr-3 text-[#471F21]/40" />
                    <span>Thaathayya Illu</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="bg-[#F4ECDE]/30 rounded-2xl p-6 mb-8 border border-[#471F21]/5">
              <h3 className="text-xs font-bold text-[#471F21]/50 uppercase tracking-widest mb-4">Payment Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#5a3a3c]">Session Price</span>
                  <span className="text-[#471F21] font-bold">₹{(booking.total_amount || booking.amount || 0).toLocaleString('en-IN')}</span>
                </div>
                {booking.deposit > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#5a3a3c]">Deposit</span>
                    <span className="text-[#471F21] font-bold">₹{booking.deposit.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-xl font-bold pt-4 border-t border-[#471F21]/10 mt-2">
                  <span className="text-[#471F21]">Total Paid</span>
                  <span className="text-[#471F21]">₹{(booking.total_amount || booking.amount || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white border-2 border-[#471F21]/10 rounded-2xl p-6">
              <h3 className="font-bold text-[#471F21] mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Important Information & Guidelines
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm text-[#5a3a3c]">
                <div className="flex items-start">
                  <span className="mr-2 text-[#471F21] font-bold">•</span>
                  <span>Arrive 10–15 mins early to utilize the slot fully.</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 text-[#471F21] font-bold">•</span>
                  <span>Time counted from gate-to-gate entry.</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 text-[#471F21] font-bold">•</span>
                  <span>Do not drag furniture; ask the caretaker for help.</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 text-[#471F21] font-bold">•</span>
                  <span>The large portrait in center must not be moved.</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 text-[#471F21] font-bold">•</span>
                  <span>No climbing on the roof – tiles are fragile.</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 text-[#471F21] font-bold">•</span>
                  <span>Any damage will attract a minimum fine of ₹2,500.</span>
                </div>
                <div className="flex items-start col-span-1 md:col-span-2 mt-2 pt-2 border-t border-[#471F21]/5 text-[#471F21] font-semibold">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5" />
                  <span>
                    {isPending
                      ? 'A confirmation message will be sent to your WhatsApp and Email once your payment is confirmed.'
                      : 'A confirmation message has been sent to your WhatsApp and Email (please check your spam folder).'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#471F21]/5 px-8 py-5 text-center border-t border-[#471F21]/10">
            <p className="text-[#471F21]/50 text-xs font-medium">This is an automated receipt for your booking at Thaathayya Illu.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => handlePrint()}
            className="flex-1 bg-white border-2 border-[#471F21]/20 text-[#471F21] px-8 py-4 rounded-2xl font-bold flex items-center justify-center hover:bg-[#F4ECDE]/30 transition-all hover:border-[#471F21]/40"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Receipt
          </button>
          <Link
            to="/booking"
            className="flex-1 bg-[#471F21] text-white px-8 py-4 rounded-2xl font-bold flex items-center justify-center hover:bg-[#5a3a3c] transition-all shadow-lg hover:shadow-xl shadow-[#471F21]/20"
          >
            Book Another Session
          </Link>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          body { background: white !important; }
          .min-h-screen { background: white !important; padding: 0 !important; }
          button, a, .text-center.mb-12 { display: none !important; }
          .max-w-3xl { max-width: 100% !important; margin: 0 !important; }
          .bg-white { border: none !important; shadow: none !important; }
          .rounded-3xl { border-radius: 0 !important; }
        }
      `}} />
    </div>
  );
};

export default PaymentSuccess;
